import dayjs, { type Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

import { EMPTY_DISPLAY_VALUE } from "constants/ui";

dayjs.extend(customParseFormat);

export type DateInput = Date | Dayjs | string | number;

/** A `LocalDate` as the API sends and receives it. */
export const API_DATE_FORMAT = "YYYY-MM-DD";

/** How every date is shown, and what a date field accepts — the masked inputs and `ui/date-picker`. */
export const INPUT_DATE_FORMAT = "MM/DD/YYYY";

const TIME_FORMAT = "h:mma";

const DUE_DATE_FORMAT = "MMM D, YYYY";

const CALENDAR_DATE = /^(\d{4}-\d{2}-\d{2}|\d{2}\/\d{2}\/\d{4})$/;

const ISO_DATE_TIME = /^\d{4}-\d{2}-\d{2}[T ]/;

const parseString = (value: string) => {
  if (CALENDAR_DATE.test(value)) {
    return dayjs(value, [API_DATE_FORMAT, INPUT_DATE_FORMAT], true);
  }

  return ISO_DATE_TIME.test(value) ? dayjs(value) : undefined;
};

/**
 * A bare calendar date is read as local midnight. `new Date("2026-08-18")` reads it as UTC midnight,
 * which west of Greenwich is still the 17th — the reason these strings must never reach `Date` directly.
 * Any other string that is not ISO 8601 is refused rather than handed to `Date`'s lenient parser, which
 * reads a half-typed `08/1` as August 1, 2001.
 */
const parse = (value: DateInput | null | undefined): Dayjs | undefined => {
  if (value === null || value === undefined || value === "") {
    return undefined;
  }

  const parsed = typeof value === "string" ? parseString(value) : dayjs(value);

  return parsed?.isValid() ? parsed : undefined;
};

/** `08/18/2026` */
export const formatDate = (value: DateInput | null | undefined, fallback = EMPTY_DISPLAY_VALUE) =>
  parse(value)?.format(INPUT_DATE_FORMAT) ?? fallback;

/** `3:05pm` */
export const formatTime = (value: DateInput | null | undefined, fallback = EMPTY_DISPLAY_VALUE) =>
  parse(value)?.format(TIME_FORMAT) ?? fallback;

/** `08/18/2026, 3:05pm` */
export const formatDateTime = (
  value: DateInput | null | undefined,
  fallback = EMPTY_DISPLAY_VALUE,
) => parse(value)?.format(`${INPUT_DATE_FORMAT}, ${TIME_FORMAT}`) ?? fallback;

/**
 * `Aug 18, 2026`. A batch's due date is the one date the design keeps spelled out; everything else is
 * `formatDate` / `formatDateTime` (#38547).
 */
export const formatDueDate = (
  value: DateInput | null | undefined,
  fallback = EMPTY_DISPLAY_VALUE,
) => parse(value)?.format(DUE_DATE_FORMAT) ?? fallback;

/**
 * `08/18/2026`. This and `toApiDate` fall back to an empty string, not `EMPTY_DISPLAY_VALUE`: their
 * result is a field value or a request payload, where a dash would be read as input.
 */
export const formatInputDate = (value: DateInput | null | undefined, fallback = "") =>
  parse(value)?.format(INPUT_DATE_FORMAT) ?? fallback;

/** A picker value. Accepts `YYYY-MM-DD` and `MM/DD/YYYY` alike, so dates stored before the ISO switch still load. */
export const toDayjs = (value: DateInput | null | undefined): Dayjs | null => parse(value) ?? null;

/**
 * A picker value as a form stores it: `YYYY-MM-DD`, or `""` once cleared. A half-typed date arrives as an
 * invalid Dayjs and is kept as "Invalid Date" rather than blanked, so the field's `pattern` reports it
 * instead of the input silently emptying.
 */
export const toFormDate = (value: Dayjs | null): string =>
  value === null ? "" : value.format(API_DATE_FORMAT);

/** `2026-08-18` */
export const toApiDate = (value: DateInput | null | undefined, fallback = "") =>
  parse(value)?.format(API_DATE_FORMAT) ?? fallback;
